import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  debounce,
  EMPTY,
  firstValueFrom,
  map,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  WarehouseResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { warehouseRouteSegment } from '../utils/warehouse-route.util';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import { bindStaffPageHeaderAction } from './staff-page-header-action.util';

@Component({
  selector: 'lib-staff-warehouses-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NotInput, FormsModule, CabinetButtonComponent],
  templateUrl: './staff-warehouses-list.component.html',
  styleUrl: './staff-warehouses-list.component.scss',
})
export class StaffWarehousesListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  readonly #warehouseService = inject(WarehouseService);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);

  protected readonly searchQuery = signal('');
  protected readonly loading = signal(true);
  protected readonly initialized = signal(false);
  protected readonly openMenuId = signal<string | null>(null);
  protected readonly headerAddActionTpl = viewChild<TemplateRef<unknown>>('headerAddAction');

  private readonly searchSubject = new Subject<string>();

  protected readonly warehouses = toSignal(
    this.searchSubject.pipe(
      tap(() => this.loading.set(true)),
      debounce((value) => (value ? timer(500) : timer(0))),
      switchMap((search) =>
        this.#warehouseService.getAdminWarehouses().pipe(
          map((response) => {
            const warehouses = this.filterWarehouses(response.data, search);
            return {
              ...response,
              warehouses,
              total: warehouses.length,
              activeTotal: warehouses.filter((w) => this.isActive(w)).length,
            };
          }),
          tap(() => {
            this.loading.set(false);
            this.initialized.set(true);
          }),
          catchError(() => {
            this.loading.set(false);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  protected readonly displayCount = computed(() => {
    if (!this.initialized()) {
      return null;
    }
    const w = this.warehouses();
    if (!w) {
      return null;
    }
    return w.activeTotal;
  });

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
    bindStaffPageHeaderAction(
      this.embeddedInCabinetPage,
      this.headerAddActionTpl,
      this.initialized,
    );
  }

  public ngOnInit(): void {

    this.searchSubject.next('');
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  protected onAdd(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'warehouses', 'create']);
  }

  protected onView(warehouse: WarehouseResponse): void {
    this.openMenuId.set(null);
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      warehouseRouteSegment(warehouse),
    ]);
  }

  protected onEdit(warehouse: WarehouseResponse): void {
    this.openMenuId.set(null);
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      warehouseRouteSegment(warehouse),
      'edit',
    ]);
  }

  protected async onDeactivate(id: string): Promise<void> {
    this.openMenuId.set(null);
    try {
      await firstValueFrom(this.#warehouseService.deactivateWarehouse(id));
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.warehouses.deactivateSuccess')],
      });
      this.searchSubject.next(this.searchQuery());
    } catch (error) {
      console.error('Failed to deactivate warehouse:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.warehouses.deactivateError')],
      });
    }
  }

  protected async onActivate(id: string): Promise<void> {
    this.openMenuId.set(null);
    try {
      await firstValueFrom(this.#warehouseService.activateWarehouse(id));
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.warehouses.activateSuccess')],
      });
      this.searchSubject.next(this.searchQuery());
    } catch (error) {
      console.error('Failed to activate warehouse:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.warehouses.activateError')],
      });
    }
  }

  protected closeMenu(): void {
    this.openMenuId.set(null);
  }

  protected isActive(warehouse: WarehouseResponse): boolean {
    return warehouse.status === 'active';
  }

  protected hasCoordinates(warehouse: WarehouseResponse): boolean {
    return (
      typeof warehouse.latitude === 'number' &&
      typeof warehouse.longitude === 'number' &&
      !!warehouse.latitude &&
      !!warehouse.longitude
    );
  }

  protected getYandexMapsUrl(warehouse: WarehouseResponse): string {
    if (!this.hasCoordinates(warehouse)) return '#';
    const { longitude, latitude } = warehouse;
    return `https://yandex.ru/maps/?pt=${longitude},${latitude}&z=17&l=map`;
  }

  private filterWarehouses(
    warehouses: WarehouseResponse[],
    search: string,
  ): WarehouseResponse[] {
    if (!search) {
      return warehouses;
    }
    const searchLower = search.toLowerCase();
    return warehouses.filter(
      (w) =>
        w.name.toLowerCase().includes(searchLower) ||
        w.city.toLowerCase().includes(searchLower) ||
        w.address.toLowerCase().includes(searchLower),
    );
  }
}
